import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import { ScheduleReminderModalStyles } from "@insite/content-library/Components/ScheduleReminderModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { DynamicDropdownPresentationProps } from "@insite/mobius/DynamicDropdown";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setModalOpen from "@waytek/Handlers/SavedOrder/SetModalOpen";
import setSavedOrderName from "@waytek/Handlers/SavedOrder/SetSaveOrderName";
import { CustomState } from "@waytek/Store/Reducers";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: SavedCartModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: CustomState) => {
    return {
        isModalOpen: state.savedOrder.isModalOpen,
        savedCartName: state.savedOrder.savedCartName,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    saveOrder,
    setSavedOrderName,
    setModalOpen,
};

export interface SavedCartModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyProps;
    listsSelect?: DynamicDropdownPresentationProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;

    /* Verndale Custom Feature */
    listsSelectNewList?: DynamicDropdownPresentationProps;
    scheduleReminderCheckbox?: CheckboxPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
}

export const savedCartModalStyles: SavedCartModalStyles = {
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    /* Verndale Custom Feature - START */
    signInMessageText: {
        as: "p",
        css: css`
            a {
                display: inline-block;
                span {
                    position: relative;
                }
            }
        `,
    },
    descriptionText: {
        as: "p",
    },
    listsSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;

                ul {
                    max-height: 205px;
                }
            `,
        },
    },
    /* Verndale Custom Feature - END */
    listsSelectNewList: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;

                ul {
                    max-height: 125px;
                }
            `,
        },
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    scheduleReminderCheckbox: {
        css: css`
            margin-top: 15px;
            align-items: flex-start;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const SavedCartModal = ({
    isModalOpen,
    savedCartName,
    saveOrder,
    setSavedOrderName,
    setModalOpen,
    extendedStyles,
    history,
    savedOrdersPageUrl,
}: Props) => {
    const [styles] = useState(() => mergeToNew(savedCartModalStyles, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    if (!isModalOpen) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalOpen({
            isModalOpen: false,
        });
    };

    const savedCartModalHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSavedOrderName({
            savedCartName: event.target.value,
        });
    };

    const addToListButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        modalCloseHandler();

        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Saved Cart Name")}
            isOpen={isModalOpen}
            handleClose={modalCloseHandler}
        >
            <StyledWrapper {...styles.innerWrapper} data-test-selector="savedCartModal">
                <TextField
                    label={translate("Saved Cart Name")}
                    value={savedCartName}
                    onChange={savedCartModalHandler}
                    {...styles.newListInput}
                    data-test-selector="savedCartName"
                />

                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.addButton}
                        onClick={e => addToListButtonClickHandler(e)}
                        data-test-selector="saveCart_addButton"
                        // disabled={inProgress}
                    >
                        {translate("Save Order")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(SavedCartModal));
